/**
 * Application constants
 */

// API
const normalizeBaseUrl = (url) => url.replace(/\/+$/, '');

const requireEnv = (key) => {
  const value = process.env[key];
  if (!value) {
    throw new Error(`${key} is required but not set. Please configure it in your environment.`);
  }
  return value;
};

export const API_BASE_URL = normalizeBaseUrl(requireEnv('REACT_APP_API_URL'));
export const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/api/v1.0';
export const SOCKET_URL = requireEnv('REACT_APP_SOCKET_URL');

// Storage Keys
export const STORAGE_KEYS = {
  TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
  THEME: 'theme_mode',
  LANGUAGE: 'language',
  SIDEBAR_STATE: 'sidebar_state',
};

// User Roles
export const USER_ROLES = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  OPERATOR_ADMIN: 'opadmin',
  OPERATOR: 'operator',
  USER: 'user',
};

// Role Labels
export const ROLE_LABELS = {
  [USER_ROLES.SUPERADMIN]: 'Super Admin',
  [USER_ROLES.ADMIN]: 'Admin',
  [USER_ROLES.OPERATOR_ADMIN]: 'Operator Admin',
  [USER_ROLES.OPERATOR]: 'Operator',
  [USER_ROLES.USER]: 'User',
};

// Message Status
export const MESSAGE_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read',
  FAILED: 'failed',
};

// Template Status
export const TEMPLATE_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

// Broadcast Status
export const BROADCAST_STATUS = {
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

// Bot Status
export const BOT_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DRAFT: 'draft',
};

// Order Status
export const ORDER_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  PREPARING: 'preparing',
  READY: 'ready',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
};

// Reservation Status
export const RESERVATION_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  NO_SHOW: 'no_show',
};

// File Types
export const FILE_TYPES = {
  IMAGE: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  DOCUMENT: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  VIDEO: ['video/mp4', 'video/webm', 'video/ogg'],
  AUDIO: ['audio/mpeg', 'audio/wav', 'audio/ogg'],
};

// File Size Limits (in bytes)
export const FILE_SIZE_LIMITS = {
  IMAGE: 5 * 1024 * 1024, // 5MB
  DOCUMENT: 10 * 1024 * 1024, // 10MB
  VIDEO: 50 * 1024 * 1024, // 50MB
  AUDIO: 10 * 1024 * 1024, // 10MB
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100],
};

// Date Formats
export const DATE_FORMATS = {
  DATE: 'DD MMM YYYY',
  DATE_TIME: 'DD MMM YYYY, hh:mm A',
  TIME: 'hh:mm A',
  DATE_INPUT: 'YYYY-MM-DD',
  TIME_INPUT: 'HH:mm',
  DATETIME_INPUT: 'YYYY-MM-DDTHH:mm',
  ISO: 'YYYY-MM-DDTHH:mm:ssZ',
};

// Theme
export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
};

// Breakpoints (matching MUI defaults)
export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

// Socket Events
export const SOCKET_EVENTS = {
  // Connection
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  CONNECT_ERROR: 'connect_error',

  // Messages
  MESSAGE_NEW: 'message:new',
  MESSAGE_SEND: 'message:send',
  MESSAGE_READ: 'message:read',
  MESSAGE_STATUS: 'message:status',

  // Typing
  TYPING_START: 'typing:start',
  TYPING_STOP: 'typing:stop',

  // Notifications
  NOTIFICATION: 'notification',

  // Broadcasts
  BROADCAST_STATUS: 'broadcast:status',

  // Rooms
  JOIN_ROOM: 'join:room',
  LEAVE_ROOM: 'leave:room',
};

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
};

// Drawer Width
export const DRAWER_WIDTH = 260;

// Header Height
export const HEADER_HEIGHT = 64;

const CONSTANTS = {
  API_BASE_URL,
  API_PREFIX,
  SOCKET_URL,
  STORAGE_KEYS,
  USER_ROLES,
  ROLE_LABELS,
  MESSAGE_STATUS,
  TEMPLATE_STATUS,
  BROADCAST_STATUS,
  BOT_STATUS,
  ORDER_STATUS,
  RESERVATION_STATUS,
  FILE_TYPES,
  FILE_SIZE_LIMITS,
  PAGINATION,
  DATE_FORMATS,
  THEME_MODES,
  BREAKPOINTS,
  SOCKET_EVENTS,
  HTTP_STATUS,
  DRAWER_WIDTH,
  HEADER_HEIGHT,
};

export default CONSTANTS;
